import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cx } from '@/lib/cx'

const GAP = 4

interface Props {
  anchor: RefObject<HTMLElement | null>
  open: boolean
  children: ReactNode
  /** `stretch` matches the anchor's width (a select); `start` sizes to content
   *  and aligns its left edge (a menu, a calendar). */
  align?: 'stretch' | 'start' | 'end'
  className?: string
  id?: string
  role?: string
  labelledBy?: string
  onKeyDown?: (event: React.KeyboardEvent) => void
}

/**
 * A layer anchored to a control but rendered at the end of `<body>`.
 *
 * Every card in this app is `overflow-hidden` — it has to be, or a flush table
 * would square off its rounded corners. A dropdown rendered inside one is
 * clipped at the card's edge, which is how a blood-group list ended up showing
 * one and a half options. A portal is the only fix that does not trade the
 * card's shape away.
 */
export function Popover({
  anchor,
  open,
  children,
  align = 'stretch',
  className,
  id,
  role,
  labelledBy,
  onKeyDown,
}: Props) {
  const panel = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setBox(null)
      return
    }
    function place() {
      const target = anchor.current
      if (!target) return
      const rect = target.getBoundingClientRect()
      const height = panel.current?.offsetHeight ?? 0
      const below = window.innerHeight - rect.bottom - GAP
      // Flip up only when there is genuinely more room there: near the middle
      // of a short viewport both sides are tight and flipping looks like a bug.
      const flip = height > below && rect.top - GAP > below
      const width = panel.current?.offsetWidth ?? rect.width
      const left =
        align === 'end'
          ? Math.min(rect.right - width, window.innerWidth - width - GAP)
          : Math.max(GAP, Math.min(rect.left, window.innerWidth - width - GAP))
      setBox({
        top: flip ? rect.top - height - GAP : rect.bottom + GAP,
        left,
        width: rect.width,
      })
    }
    place()
    // Capture phase: the scroller is usually an inner region, not the window.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, align, anchor])

  if (!open) return null

  return createPortal(
    <div
      ref={panel}
      data-popover=""
      id={id}
      role={role}
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        top: box?.top ?? -9999,
        left: box?.left ?? -9999,
        ...(align === 'stretch' ? { width: box?.width } : {}),
        // Invisible for the first frame, while the height is being measured.
        visibility: box ? 'visible' : 'hidden',
      }}
      // The layer lives here rather than on whatever the caller renders
      // inside: `z-index` is ignored on a static element, so a panel that
      // set its own was silently painting at the default depth.
      className={cx('z-popover', className)}
    >
      {children}
    </div>,
    document.body,
  )
}
